import { Badge } from '@/components/common/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import { TabsContent, TabsTrigger } from '@/components/common/tabs'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { Vault } from '@/types/vault'
import { formatTokenValue } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { z } from 'zod'

export const AERA_PENDING_TAB_NAME = 'aeraPending'

const pendingRequestsSchema = z.object({
  deposits: z.array(
    z.object({
      amount: z.string(),
      deadline: z.string(),
    })
  ),
  redeems: z.array(
    z.object({
      amount: z.string(),
      deadline: z.string(),
    })
  ),
})

export type AeraSpecificData = {
  pendingRequests: z.infer<typeof pendingRequestsSchema>
}

export function AeraPendingTab({
  vault,
  transactionsContext,
}: {
  vault: Vault
  transactionsContext: TransactionsContext
}) {
  const pendingRequests = useMemo(() => {
    return pendingRequestsSchema.parse((transactionsContext?.vaultSpecificData as AeraSpecificData)['pendingRequests'])
  }, [transactionsContext])

  return (
    <TabsContent value={AERA_PENDING_TAB_NAME} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
      {pendingRequests && (
        <Table>
          <TableHeader>
            <TableRow variant="header">
              <TableHead>Action</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingRequests.deposits.map((deposit) => (
              <TableRow>
                <TableCell>
                  <p className="font-bold text-heading-foreground text-m">Deposit</p>
                </TableCell>
                <TableCell>
                  {formatTokenValue(
                    { value: deposit.amount, decimals: vault.asset.decimals },
                    { minimumFractionDigits: 2 }
                  )}{' '}
                  {vault.asset.symbol}
                </TableCell>
                <TableCell>
                  <Badge color={'orange'}>Pending</Badge>
                </TableCell>
              </TableRow>
            ))}
            {pendingRequests.redeems.map((redeem) => (
              <TableRow>
                <TableCell>
                  <p className="font-bold text-heading-foreground text-m">Redeem</p>
                </TableCell>
                <TableCell>
                  {formatTokenValue(
                    { value: redeem.amount, decimals: vault.asset.decimals },
                    { minimumFractionDigits: 2 }
                  )}{' '}
                  {vault.asset.symbol}
                </TableCell>
                <TableCell>
                  <Badge color={'orange'}>Pending</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div className="flex flex-col gap-2 text-center">
        <p className="bold font-bold text-heading-foreground text-m">Actions in progress...</p>
        <div className="text-gray-500 text-sm">
          Some of the actions on this vault can take up to 24 hours to complete.
        </div>
      </div>
    </TabsContent>
  )
}

export const AeraPendingTabTrigger = ({ className }: { className?: string }) => {
  return (
    <TabsTrigger value={AERA_PENDING_TAB_NAME} className={className}>
      Pending
    </TabsTrigger>
  )
}
